import { useFeatureValue } from '@growthbook/growthbook-react';

import { useSettings } from '@/hooks/state/useSettings';
import { normalizeIsoCountry } from '@/lib/geo/country';
import type { ValidCurrency } from '@/types/plans';

import { type Cohort, coerceCohort } from './cohorts';
import { getCohortFromPlanId } from './config';
import { getPricingTestFlagKey } from './flags';
import { usePricingCohortInitial } from './PricingCohortContext';

// React hooks must be called unconditionally, so we always call
// `useFeatureValue` — for currencies with no test (CAD), we read a
// guaranteed-unset noop key to keep the hook count stable.
const NOOP_FLAG_KEY = 'pricing-test-noop';

export function usePricingCohort(): {
  currency: ValidCurrency;
  country: string;
  cohort: Cohort;
} {
  const { settings } = useSettings();
  // When the pricing page renders, it provides the SSR-resolved currency /
  // country so that the very first client evaluation reads the same flag the
  // server did. Without this, `useFeatureValue` fires `experiment_viewed`
  // against `pricing-test-usd-row` (the default-USD / empty-country flag)
  // before `settings` loads, double-enrolling visitors.
  const initial = usePricingCohortInitial();

  // Use `||` (not `??`) so an empty-string `settings.currency.code` —
  // possible for authenticated users without a backend-supplied currency —
  // falls through to the SSR-resolved initial value. Otherwise the hook
  // would return `''` despite SSR having stamped a real currency into the
  // prefetched data, re-introducing the cohort divergence this hook is
  // meant to prevent.
  const currency = (settings?.currency.code ||
    initial?.initialCurrency ||
    'USD') as ValidCurrency;
  // `settings.country.iso_code_2` is the canonical ISO 3166-1 alpha-2
  // source; `settings.user.country` is a display-name string ("United
  // States") and must NOT be used for cohort routing — `getCurrencyBucket`
  // expects ISO codes and silently mis-routes display names into
  // `pricing-test-usd-row`.
  const country =
    normalizeIsoCountry(settings?.country?.iso_code_2) ||
    initial?.initialCountry ||
    '';

  // Active subscribers: derive the cohort from their Stripe plan ID. The
  // plan they were billed on is the cohort they were assigned to when they
  // subscribed — `COHORT_CONFIG` is a 1-to-1 (bucket, cohort, tier) → plan-ID
  // mapping, so the reverse lookup is lossless. Without this, GrowthBook
  // targeting (which gates on `subscriptionType = "Free"`) stops matching
  // post-conversion and every other tier shown to the user (upgrade modal,
  // switch-to-yearly, other cards on `/pricing`) reverts to the Phase-1
  // baseline prices, under-pricing upgrades for Aggressive/Moderate cohorts.
  const planId = settings?.subscriptionData?.plan;
  const isActiveSub = settings?.subscriptionData?.isActive ?? false;
  const inferredCohort =
    isActiveSub && typeof planId === 'number'
      ? getCohortFromPlanId(planId)
      : null;

  const flagKey = getPricingTestFlagKey(currency, country);
  // When we can derive the cohort from the plan ID, read the noop flag so we
  // don't fire a fresh `experiment_viewed` for a user who has already paid —
  // they aren't being newly enrolled, we're just resolving display prices.
  const lookupKey =
    inferredCohort === null ? (flagKey ?? NOOP_FLAG_KEY) : NOOP_FLAG_KEY;
  // Called unconditionally to keep hook order stable across renders. The
  // return value is intentionally ignored when `inferredCohort !== null`;
  // the plan-ID-derived cohort wins in that branch.
  const value = useFeatureValue<string>(lookupKey, 'control');

  if (inferredCohort !== null) {
    return { currency, country, cohort: inferredCohort };
  }

  return {
    currency,
    country,
    cohort: flagKey ? coerceCohort(value) : 'control',
  };
}
